import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { Checkbox, Button, Tag, Space, Input, Tooltip, Spin } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { apiPost } from "api/callApi";
import theme from "theme";
import { showAlert, getFolderRedirectUrl } from "utils/helper";
import { useNavigate } from "react-router";
import DeleteComponent from "views/AdminSettings/DeleteComponent";
import InnoIcons from "InnoUI/innoicons";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const ProcessingOverlay = styled.div`
  position: absolute;
  inset: 50px 0 0;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.78);
  color: ${theme.colors.themeColor};
  font-size: 13px;
  font-weight: 600;
  backdrop-filter: blur(2px);
`;

const DrawerTitlesContainer = styled.div`
  display: flex;
  background: ${theme.gradient.themeGradient};
  align-items: center;
  justify-content: space-between;
  padding: 0px 10px 0px 20px;
  height: 50px;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const DrawerTitles = styled.div`
  font-size: 17px;
  color: white;
  font-weight: normal;
  align-items: center;
  display: flex;
  gap: 6px;
`;

const CloseIcon = styled.p`
  cursor: pointer;
  color: white;
  margin: 0;
`;

const ScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0px 20px 16px 20px;
`;

const LinkedItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 450px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.themeColor};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.bordertask};
  }
`;

const LinkedItem = styled.div`
  padding: 12px;
  border: 1px solid ${theme.colors.bordertask};
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${(props) => (props.$hasData ? "#fff5f5" : "#fafafa")};
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$hasData ? "#ffe7e7" : "#f0f0f0")};
  }
`;

const CheckboxWrapper = styled.div`
  flex-shrink: 0;
`;

const ThemedCheckbox = styled(Checkbox)`
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${theme.colors.themeColor};
    border-color: ${theme.colors.themeColor};
  }
  .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    background-color: ${theme.colors.themeColor};
  }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: ${theme.colors.themeColor};
  }
`;

const ItemContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemName = styled.div`
  font-weight: 500;
  font-size: 14px;
  color: ${theme.colors.textPrimary};
`;

const ItemType = styled.span`
  font-size: 12px;
  color: ${theme.colors.textSecondary};
  text-transform: uppercase;
`;

const ItemStatus = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 12px;
`;

const ReviewLink = styled.a`
  font-size: 12px;
  color: ${theme.gradient.themeGradient};
  text-decoration: underline;
  cursor: pointer;
  margin-left: auto;
  white-space: nowrap;
  padding-left: 10px;

  &:hover {
    opacity: 0.8;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px;
  color: ${theme.colors.textSecondary};
  font-size: 14px;
`;

const Footer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 14px 20px 0px 20px;
  border-top: 1px solid ${theme.colors.bordertask};
  background: #fff;
`;

const shimmerMove = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const shimmerBase = css`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 600px 100%;
  animation: ${shimmerMove} 1.4s infinite linear;
  border-radius: 4px;
`;

const ShimmerRow = styled.div`
  padding: 12px;
  border: 1px solid ${theme.colors.bordertask};
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fafafa;
`;

const ShimmerBox = styled.div`
  ${shimmerBase}
  width: ${(p) => p.$w || "100%"};
  height: ${(p) => p.$h || "14px"};
  flex-shrink: 0;
`;

const ShimmerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FolderDeleteReview = ({
  folderId,
  folderName,
  folderType,
  onConfirmDelete,
  onCancel,
  isDeleting = false,
}) => {
  const [linkedFolders, setLinkedFolders] = useState(null);
  const [selectedOccupants, setSelectedOccupants] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDeleteView, setOpenDeleteView] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const fetchLinkedFolders = async () => {
    try {
      setLoading(true);
      setSelectedOccupants([]);
      setSelectedProjects([]);
      const response = await apiPost(
        `/v1/projectfolder/${folderId}/linked-folders`,
      );
      if (response.data) {
        setLinkedFolders(response.data);
      }
    } catch (error) {
      console.error("Error fetching linked folders:", error);
      showAlert({
        title: "Error",
        text: "Failed to load linked occupants",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (folderId) {
      fetchLinkedFolders();
    }
  }, [folderId]);

  const handleSelectOccupant = (occupantId) => {
    setSelectedOccupants((prev) =>
      prev.includes(occupantId)
        ? prev.filter((id) => id !== occupantId)
        : [...prev, occupantId],
    );
  };

  const handleSelectProject = (projectId) => {
    setSelectedProjects((prev) =>
      prev.includes(projectId)
        ? prev.filter((id) => id !== projectId)
        : [...prev, projectId],
    );
  };

  const allItems = linkedFolders?.linked || [];
  // const allItems = Array.from({ length: 100 }, (_, index) => ({
  //   id: index + 1,
  //   name: `Folder ${index + 1}`,
  // }));

  const filteredItems = allItems.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectableItems = allItems.filter((item) => !item.hasFiles);
  const allOccupants = selectableItems
    .filter((item) => item.projectType === 1)
    .map((item) => item.id);
  const allProjects = selectableItems
    .filter((item) => item.projectType === 0)
    .map((item) => item.id);

  const handleSelectAll = () => {
    const allSelected =
      selectedOccupants.length === allOccupants.length &&
      selectedProjects.length === allProjects.length;

    if (allSelected) {
      setSelectedOccupants([]);
      setSelectedProjects([]);
    } else {
      setSelectedOccupants(allOccupants);
      setSelectedProjects(allProjects);
    }
  };

  const handleNavigateToFolder = (item) => {
    const url = getFolderRedirectUrl(
      folderType,
      item.id,
      item.folderId,
      item.projectType,
    );
    if (url) window.open(url, "_blank");
  };

  const handleDelete = () => {
    setOpenDeleteView(true);
  };

  const confirmDelete = () => {
    if (isDeleting) return Promise.resolve();
    if (onConfirmDelete) {
      return onConfirmDelete({
        deleteLinkedOccupants: selectedOccupants,
        deleteLinkedProjects: selectedProjects,
      });
    }
    return Promise.resolve();
  };

  const hasLinked = allItems.length > 0;
  const hasData = allItems.some((item) => item.hasData);
  const selectedCount = selectedOccupants.length + selectedProjects.length;
  const totalCount = allOccupants.length + allProjects.length;

  return (
    <Wrapper>
      {isDeleting && (
        <ProcessingOverlay>
          <Spin size="large" />
          <span>Deleting folder, please wait...</span>
        </ProcessingOverlay>
      )}

      <DrawerTitlesContainer>
        <DrawerTitles>
          <span>Delete Folder:</span>
          <strong>{folderName}</strong>
          {allItems.length > 0 && (
            <span
              style={{
                fontSize: 12,
                fontWeight: "normal",
                opacity: 0.85,
                marginLeft: 6,
              }}
            >
              ({allItems.length} linked)
            </span>
          )}
        </DrawerTitles>
        <CloseIcon>
          <InnoIcons.CloseIcon onClick={onCancel} />
        </CloseIcon>
      </DrawerTitlesContainer>

      <Footer>
        <Button onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          type="primary"
          danger
          onClick={handleDelete}
          disabled={selectedCount === 0 || isDeleting}
          loading={isDeleting}
          style={{
            background:
              selectedCount === 0 ? undefined : theme.colors.dangerColor,
            borderColor:
              selectedCount === 0 ? undefined : theme.colors.dangerColor,
          }}
        >
          {isDeleting
            ? "Deleting..."
            : `Delete Folder${
                selectedCount > 0 ? ` + ${selectedCount} Item(s)` : ""
              }`}
        </Button>
      </Footer>

      <ScrollBody>
        {loading ? (
          <LinkedItemsContainer>
            {Array.from({ length: 4 }).map((_, i) => (
              <ShimmerRow key={i}>
                <ShimmerBox $w="16px" $h="16px" />
                <ShimmerContent>
                  <ShimmerBox $w="60%" $h="14px" />
                  <ShimmerBox $w="30%" $h="11px" />
                </ShimmerContent>
                <ShimmerBox $w="56px" $h="12px" />
              </ShimmerRow>
            ))}
          </LinkedItemsContainer>
        ) : !hasLinked ? (
          <EmptyState>
            <p>No linked occupants or projects found.</p>
            <p>You can proceed with deleting this folder.</p>
          </EmptyState>
        ) : (
          <>
            <p
              style={{
                fontSize: 13,
                color: theme.colors.textSecondary,
                marginBottom: 12,
              }}
            >
              This folder is linked to the following occupants and/or projects.
              Select which ones should also be deleted:
            </p>

            {hasData && (
              <div
                style={{
                  padding: "10px 12px",
                  background: "#fff1f0",
                  border: "1px solid #ffccc7",
                  borderRadius: "4px",
                  marginBottom: 14,
                  fontSize: 12,
                  color: "#d9534f",
                }}
              >
                ⚠️ Some linked items contain files. Review and delete the files
                first, then refresh to enable selection.
              </div>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
                gap: 12,
              }}
            >
              <ThemedCheckbox
                onChange={handleSelectAll}
                checked={selectedCount === totalCount && totalCount > 0}
                indeterminate={selectedCount > 0 && selectedCount < totalCount}
              >
                <strong>Select all</strong>
              </ThemedCheckbox>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Input
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                  style={{ width: 300, fontSize: 13 }}
                />
                <Button
                  onClick={fetchLinkedFolders}
                  loading={loading}
                  disabled={loading || isDeleting}
                  size="small"
                  icon={<ReloadOutlined />}
                >
                  Refresh
                </Button>
              </div>
            </div>

            {filteredItems.length === 0 ? (
              <EmptyState>No results for "{searchTerm}"</EmptyState>
            ) : (
              <LinkedItemsContainer>
                {filteredItems.map((item) => {
                  const isSelected =
                    item.projectType === 1
                      ? selectedOccupants.includes(item.id)
                      : selectedProjects.includes(item.id);

                  return (
                    <LinkedItem
                      key={`${item.projectType}-${item.id}`}
                      $hasData={item.hasData}
                    >
                      <CheckboxWrapper>
                        <Tooltip
                          title={
                            item.hasFiles
                              ? "This folder contains files. Review and delete files first, then refresh."
                              : ""
                          }
                          placement="top"
                        >
                          <ThemedCheckbox
                            checked={isSelected}
                            disabled={item.hasFiles}
                            onChange={() =>
                              item.projectType === 1
                                ? handleSelectOccupant(item.id)
                                : handleSelectProject(item.id)
                            }
                          />
                        </Tooltip>
                      </CheckboxWrapper>

                      <ItemContent>
                        <ItemName>{item.name}</ItemName>
                        <Space size={6}>
                          <ItemType>
                            {item.projectType === 0 ? "PROJECT" : "OCCUPANT"}
                          </ItemType>
                          <ItemStatus>
                            <Tag
                              color={item.hasFiles ? "blue" : "default"}
                              style={{ margin: 0 }}
                            >
                              {item.fileCount ?? 0} File
                              {(item.fileCount ?? 0) !== 1 ? "s" : ""}
                            </Tag>
                            {item.hasChildren && (
                              <Tag color="cyan" style={{ margin: 0 }}>
                                Has Subfolders
                              </Tag>
                            )}
                          </ItemStatus>
                        </Space>
                      </ItemContent>

                      {/* <ReviewLink
                        onClick={() => handleNavigateToFolder(item.reviewUrl)}
                      >
                        Review →
                      </ReviewLink> */}
                      <ReviewLink onClick={() => handleNavigateToFolder(item)}>
                        Review →
                      </ReviewLink>
                    </LinkedItem>
                  );
                })}
              </LinkedItemsContainer>
            )}
          </>
        )}
      </ScrollBody>

      <DeleteComponent
        title={"Delete Folder"}
        messageData={`Are you sure you want to delete "${folderName}"${selectedCount > 0 ? ` and ${selectedCount} linked item(s)` : ""}?`}
        setOpenDeleteView={setOpenDeleteView}
        openDeleteView={openDeleteView}
        handleDelete={confirmDelete}
        handleArchive={() => setOpenDeleteView(false)}
        firstButtontext={"Cancel"}
        isLoading={isDeleting}
      />
    </Wrapper>
  );
};

export default FolderDeleteReview;
